import { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { OreNavBarHeaderDivider } from '@edenredespana/oreneta';
import { MultiWebClientMerchantModel, MultiWebClientSubItemModel } from '@/User/domain';
import { useSelectedMerchant } from './hooks/useSelectedMerchant';
import { useDropdownPosition } from './hooks/useDropdownPosition';
import { useDropdownOpen } from './hooks/useDropdownOpen';
import { useMerchantFilter } from './hooks/useMerchantFilter';
import { SearchBar } from './molecules/SearchBar';
import { EmptyState } from './atoms/EmptyState';
import { DropdownTrigger } from './atoms/DropdownTrigger';
import { MerchantList } from './molecules/MerchantList';
import { DROPDOWN_STYLES } from './constants/styles';
import { useTranslation } from 'react-i18next';
import { headerNavbarTranslation } from '../navigation/navigationTranslation';

export const DropdownSearch = (props:{
  userMultiWeb: MultiWebClientMerchantModel[], 
  isNursery: boolean,
  setActiveMultiWebMerchant: (activeMerchantId: number) => Promise<boolean>
}) => {
  const { t } = useTranslation();
  const { userMultiWeb, setActiveMultiWebMerchant } = props;
  const [search, setSearch] = useState('');
  const [hoveredMainKey, setHoveredMainKey] = useState<string | null>(null);
  const [hoveredSubKey, setHoveredSubKey] = useState<string | null>(null);

  // Refs para el trigger y dropdown
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Hooks personalizados
  const { selected, handleSetSelected: baseHandleSetSelected } = useSelectedMerchant(userMultiWeb, setActiveMultiWebMerchant);
  const { open, setOpen } = useDropdownOpen(triggerRef, dropdownRef);
  const dropdownPos = useDropdownPosition(open, triggerRef);
  const filtered = useMerchantFilter(userMultiWeb, search);

  // Wrapper para handleSetSelected que también cierra el dropdown
  const handleSetSelected = async (item: MultiWebClientMerchantModel | MultiWebClientSubItemModel) => {
    setOpen(false);
    await baseHandleSetSelected(item);
  };

  return (
    <OreNavBarHeaderDivider>
      <div ref={triggerRef}>
        <DropdownTrigger
          selected={selected}
          open={open}
          onClick={() => setOpen(o => !o)}
        />
      </div>
      {/* Dropdown usando portal para evitar problemas de stacking/contexto */}
      {open && createPortal(
        <div 
          ref={dropdownRef}
          style={{
            ...DROPDOWN_STYLES.dropdownContainer,
            position: 'fixed',
            top: dropdownPos.top,
            left: dropdownPos.left,
            width: dropdownPos.width,
          }}>
          <SearchBar
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t(headerNavbarTranslation.DropdownSearch.placeholder)}
          />
          <div style={DROPDOWN_STYLES.divider} />
          <div style={DROPDOWN_STYLES.contentSection}>
            {filtered.length > 0 ? (
              <MerchantList
                merchants={filtered}
                hoveredMainKey={hoveredMainKey}
                hoveredSubKey={hoveredSubKey}
                onMerchantClick={handleSetSelected}
                onSubItemClick={handleSetSelected}
                onMerchantMouseEnter={setHoveredMainKey}
                onMerchantMouseLeave={() => setHoveredMainKey(null)}
                onSubItemMouseEnter={setHoveredSubKey}
                onSubItemMouseLeave={() => setHoveredSubKey(null)}
              />
            ) : (
              <EmptyState isNursery={props.isNursery} />
            )}
            <div style={DROPDOWN_STYLES.spacer} />
          </div>
        </div>,
        document.body
      )}
    </OreNavBarHeaderDivider>
  );
}
